import * as React from 'react';
import {
    Show,
    TextField,
    DateField,
    TopToolbar,
    EditButton,
    DeleteButton,
    useRecordContext,
    TabbedShowLayout,
    Tab
} from 'react-admin';
import { codesFields } from '../codes.model';
import CodeType from './fields/codeType.component';
import DefaultPlaceholderField from '../../material/defPlaceholderField.component';
import OrganizationLinkField from './fields/codeOrganizationLink.component';
import CodeRedeemed from './fields/codeRedeemed.component';
import YesNoField from '../../material/yesNoField.component';
import UserInfoField from './fields/codeUserInfo.component';
import SupportedRoles from './fields/codeSupportedRoles.component';
import CodeProductFeatures from './fields/codeProductFeatures.component';
import { DATE_TIME_OPTIONS } from '../../app/app.component';
import { LabeledField } from '../../components/layouts';
import { useGrowthBook } from '@growthbook/growthbook-react';

const ShowActions = () => (
    <TopToolbar>
        <EditButton />
    </TopToolbar>
);

const CodeTitle = () => {
    const record = useRecordContext();
    return record ? `Code: ${record[codesFields.codeName]}` : '';
};

const CodeShow = () => {
    const growthbook = useGrowthBook();
    
    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <Show
            title={<CodeTitle />}
            actions={<ShowActions />}
            // The codes list query does not return role_ids, but react-admin primes
            // the getOne cache from list results. With the global 5-min staleTime,
            // navigating list -> show serves that partial record (role_ids: []) and
            // never refetches, so Supported Roles is empty until a hard refresh.
            // Force a fresh fetch so role_ids (only present in getOne) is loaded.
            queryOptions={{ staleTime: 0 }}
            sx={{
                '& .RaShow-main': {
                    margin: '0.5em'
                }
            }}
        >
            <TabbedShowLayout>
                {/* Details Tab */}
                <Tab label="Details">
                    <LabeledField label="Id">
                        <DefaultPlaceholderField 
                            source={codesFields.id} 
                            wrapField={<TextField/>} 
                        />
                    </LabeledField>
                    <LabeledField label="Organization">
                        <OrganizationLinkField />
                    </LabeledField>
                    <LabeledField label="Code Name">
                        <DefaultPlaceholderField 
                            source={codesFields.codeName} 
                            wrapField={<TextField/>}  
                        />
                    </LabeledField>
                    <LabeledField label="Code Type">
                        <DefaultPlaceholderField 
                            source={codesFields.codeType} 
                            wrapField={<CodeType/>}  
                        />
                    </LabeledField>
                    <LabeledField label="Subscription Plan">
                        <DefaultPlaceholderField 
                            source={codesFields.planName} 
                            wrapField={<TextField/>} 
                        />
                    </LabeledField>
                    <LabeledField label="Invalidated">
                        <YesNoField 
                            source={codesFields.invalidated} 
                        />
                    </LabeledField>
                    <LabeledField label="Redeemed">
                        <CodeRedeemed 
                            source={codesFields.redeemed} 
                        />
                    </LabeledField>
                    <LabeledField label="Created At">
                        <DefaultPlaceholderField 
                            source={codesFields.createdAt} 
                            options={DATE_TIME_OPTIONS} 
                            wrapField={<DateField/>} 
                        />
                    </LabeledField>
                    <LabeledField label="User">
                        <UserInfoField 
                            source={codesFields.userId} 
                        />
                    </LabeledField>
                </Tab>

                {/* Roles Tab */}
                <Tab label="Roles">
                    <LabeledField label="Supported Roles">
                        <SupportedRoles />
                    </LabeledField>
                </Tab>

                {/* Product Features Tab (conditionally rendered) */}
                {growthbook && growthbook.isOn('enableAdminOrganizationFeatureToggles') && (
                    <Tab label="Product Features">
                        <LabeledField label="Enabled Features">
                            <CodeProductFeatures />
                        </LabeledField>
                    </Tab>
                )}
            </TabbedShowLayout>
        </Show>
    );
};

export default CodeShow;
